import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { Button } from "../../../components/forms/Buttons/Button";
import Input from "../../../components/forms/Input/Input";
import Select from "../../../components/forms/Select/Select";
import { postTeam } from "../../../services/olympiad/teamService";
import { getRegions } from "../../../services/olympiad/regionService";
import RulesOfTeam from "../RulesOfTeam";

export default function CreateTeamForm({ onTeamCreated }) {
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm();

  const watchedRegion = watch("region");

  useEffect(() => {
    const fetchOptions = () => {
      setLoadingOptions(true);
      Promise.all([getRegions({ is_full: false })])
        .then(([regionsData]) => {
          setRegions(regionsData.results || []);
        })
        .catch((error) => {
          console.error("Error fetching options:", error);
        })
        .finally(() => {
          setLoadingOptions(false);
        });
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (watchedRegion) {
      const region = regions.find((r) => r.uuid === watchedRegion);
      setSelectedRegion(region || null);
    } else {
      setSelectedRegion(null);
    }
  }, [watchedRegion, regions]);

  const onSubmit = (data) => {
    setLoading(true);
    const teamData = {
      name: data.teamName,
      year: data.year,
      region_uuid: data.region,
      school: data.school,
    };

    // Only include district and city if region is not republican
    if (selectedRegion && !selectedRegion.is_republican_city) {
      if (data.district) {
        teamData.district = data.district;
      }
      if (data.city) {
        teamData.city = data.city;
      }
    }

    postTeam(teamData)
      .then((response) => {
        onTeamCreated(response);
      })
      .catch((error) => {
        console.error("Error creating team:", error);
        alert(error.response?.data?.detail || t("team.error_creating_team"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const regionOptions = regions.map((region) => ({
    label: region.name || region.uuid,
    value: region.uuid,
  }));

  return (
    <div className="bg-white rounded-lg border border-gray-300 p-8">
      <h2 className="text-xl font-semibold mb-4">{t("team.create_team")}</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div>
          <Input
            label={t("team.team_name")}
            placeholder={t("team.enter_team_name")}
            errorMsg={errors?.teamName?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.teamName?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="text"
            required
            {...register("teamName", {
              required: t("team.team_name_required"),
              minLength: {
                value: 3,
                message: t("team.team_name_min_length"),
              },
              maxLength: {
                value: 50,
                message: t("team.team_name_max_length"),
              },
            })}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t("team.team_name_hint")}{" "}
          </p>
        </div>
        <div>
          <Controller
            name="region"
            control={control}
            rules={{ required: t("team.region_required") }}
            render={({ field }) => (
              <Select
                label={t("team.region")}
                placeholder={t("team.select_region")}
                options={regionOptions}
                value={field.value}
                onChange={(option) => field.onChange(option?.value)}
                errorMsg={errors?.region?.message}
                loading={loadingOptions}
                required
                className="w-full text-sm"
              />
            )}
          />

          <p className="text-xs text-gray-500 mt-1">
            {t("team.region_not_in_list_hint")}
          </p>
        </div>

        {selectedRegion && !selectedRegion.is_republican_city && (
          <Input
            label={t("team.district")}
            placeholder={t("team.enter_district")}
            errorMsg={errors?.district?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.district?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="text"
            required
            {...register("district", {
              required: t("team.district_required"),
            })}
          />
        )}

        {selectedRegion && !selectedRegion.is_republican_city && (
          <Input
            label={t("team.city")}
            placeholder={t("team.enter_city")}
            errorMsg={errors?.city?.message}
            inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
              errors?.city?.message && "ring-error ring-1"
            }`}
            className="w-full text-sm"
            type="text"
            required
            {...register("city", {
              required: t("team.city_required"),
            })}
          />
        )}

        <Input
          label={t("team.school")}
          placeholder={t("team.enter_school")}
          errorMsg={errors?.school?.message}
          inputClassName={`bg-white shadow-none outline-1 outline-gray-300 md:rounded-lg h-10 ${
            errors?.school?.message && "ring-error ring-1"
          }`}
          className="w-full text-sm"
          type="text"
          required
          {...register("school", {
            required: t("team.school_required"),
          })}
        />

        <div className="mt-4">
          <Button
            className="w-full text-base font-semibold"
            text={t("team.create_team")}
            color="primary"
            type="submit"
            loading={loading || loadingOptions}
            disabled={loading || loadingOptions}
          />
        </div>
      </form>
    </div>
  );
}
